import { isOddsEffectivelyOne } from "../oddsUtils";
import { isMatchFinishedStatus } from "./decimalEventStatus";
import { getTraderBatsmanMarketHideReason } from "./cricketTraderBatsmanBoardFilter";

export {
  PREMATCH_BATSMAN_TYPE_IDS,
  INPLAY_BATSMAN_LINE_TYPE_IDS,
  isPrematchBatsmanPropMarket,
  isInPlayBatsmanLineMarket,
  isDecimalBatterDismissedStatus,
  isTraderCricketMatchInPlay,
  batsmanMarketPlayerName,
  isBatsmanLineForDismissedBatter,
  getTraderBatsmanMarketHideReason,
} from "./cricketTraderBatsmanBoardFilter";

/** Missing or explicit 0 — treated as off (same as Decimal 0). */
function isDecimalFlagZeroOrMissing(v) {
  return v === undefined || v === null || v === 0 || v === "0";
}

/** Explicit 1 only (missing is not 1). */
function isDecimalFlagOne(v) {
  return v === 1 || v === "1";
}

/**
 * Delta Appendix: selection `active` — 1 = displayed; missing or 0 = not displayed (hide runner).
 * @param {Object} selection - selection from market_update
 * @returns {boolean} true only when active is explicitly 1
 */
export function isSelectionActive(selection) {
  if (!selection) return false;
  return isDecimalFlagOne(selection.active);
}

/**
 * Selections to render per Decimal: only `active === 1`. Market-level `active=0` is handled separately (SUS all).
 * @param {Object|null|undefined} marketOrPrice
 * @returns {Object[]}
 */
export function getCricketDisplayableSelections(marketOrPrice) {
  const list = marketOrPrice?.selectionslist ?? marketOrPrice?.selections ?? [];
  if (!Array.isArray(list)) return [];
  if (isCricketMatchWinnerForTrade(marketOrPrice)) {
    return list.filter((s) => isSelectionActive(s) || hasValidCricketRecommendedOdds(s));
  }
  return list.filter((s) => isSelectionActive(s));
}

export function isCricketMatchWinnerForTrade(market) {
  if (!market) return false;
  const tid = String(market.market_type_id || "");
  return (
    market.market_type?.toLowerCase().includes("match winner") ||
    (market.market_name || market.name || "").toLowerCase().includes("match winner") ||
    tid === "10" ||
    tid === "1010" ||
    tid === "11" ||
    tid === "15"
  );
}

/**
 * Market offerable per Decimal Integration Guide: display=1 and active=1 (Match Winner exempt).
 */
export function isCricketMarketOfferableForTrade(market) {
  if (!market) return false;
  if (isCricketMatchWinnerForTrade(market)) return true;
  return isDecimalFlagOne(market.display) && isDecimalFlagOne(market.active);
}

/**
 * User-facing block reason for trade submit, or null if OK.
 * @param {Object|null|undefined} market
 * @param {Object|null|undefined} selection
 * @param {Object|null|undefined} [marketOrPrice]
 */
export function getCricketMarketTradeBlockReason(market, selection, marketOrPrice = null, liveCtx = null) {
  const m = marketOrPrice || market;
  const settled = getSettledMarketTradeBlockReason(m);
  if (settled) return settled;
  const batsmanHide = getTraderBatsmanMarketHideReason(m, liveCtx || {});
  if (batsmanHide) return batsmanHide;
  if (!isCricketMarketOfferableForTrade(m)) {
    if (isDecimalFlagZeroOrMissing(m?.display)) return "This market is not available for trading.";
    if (isDecimalFlagZeroOrMissing(m?.active)) return "This market is currently suspended.";
    return "This market is not available for trading.";
  }
  if (selection && !isSelectionActive(selection)) {
    return "This selection is not available for trading.";
  }
  return null;
}

/**
 * Market or line (from market_update) must have `active === 1` to be non-suspended; missing or 0 → SUS.
 * @param {Object|null|undefined} marketOrLine
 * @returns {boolean}
 */
export function isCricketMarketActiveOne(marketOrLine) {
  if (!marketOrLine) return false;
  return isDecimalFlagOne(marketOrLine.active);
}

/**
 * @param {Object|null|undefined} marketOrLine
 * @returns {boolean} true when market/line should show suspended (active missing or not 1)
 */
export function isCricketMarketSuspendedByActive(marketOrLine) {
  return !isCricketMarketActiveOne(marketOrLine);
}

/**
 * List/filter: only show a market in the UI when `display` is explicitly 1.
 * @param {Object|null|undefined} market
 * @returns {boolean}
 */
export function isCricketMarketDisplayOne(market) {
  if (!market) return false;
  return isDecimalFlagOne(market.display);
}

/**
 * List/filter: display=1 (missing ⇒ 0); active 1 or 0/missing (missing active ⇒ suspend/SUS).
 * Matches backend `isCricketMarketVisibleOrSuspended`.
 * @param {Object|null|undefined} market
 * @returns {boolean}
 */
export function isCricketMarketVisibleInList(market) {
  if (!market) return false;
  if (isDecimalFlagZeroOrMissing(market.display)) return false;
  return true;
}

const SETTLED_STATUS_NUMERIC = { 1: "UNSETTLED", 2: "SETTLED", 3: "VOID", 4: "DEADHEAT" };

function normalizeDecimalSettlementStatusRaw(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return SETTLED_STATUS_NUMERIC[raw] ?? null;
  const s = String(raw).trim();
  const upper = s.toUpperCase();
  if (upper === "UNSETTLED" || upper === "SETTLED" || upper === "VOID" || upper === "DEADHEAT") return upper;
  const n = parseInt(s, 10);
  if (!Number.isNaN(n) && n >= 1 && n <= 4) return SETTLED_STATUS_NUMERIC[n] ?? null;
  return null;
}

/** Appendix 4 from `settled_status` / `Settled_status` / Delta `status` (aligns with backend `cricket-settled-status`). */
export function cricketMarketAppendix4StatusForDisplay(market) {
  if (!market || typeof market !== "object") return null;
  for (const raw of [market.settled_status, market.Settled_status, market.status]) {
    if (raw == null || raw === "") continue;
    const normalized = normalizeDecimalSettlementStatusRaw(raw);
    if (normalized) return normalized;
  }
  return null;
}

/**
 * List/filter: hide only when Appendix 4 is explicitly SETTLED, VOID, or DEADHEAT.
 * Missing/empty status ⇒ unsettled (matches backend `isUnsettled` / `filterAllowedMarkets`).
 * Trading labels on `status` (ACTIVE / PAUSED) do not map to Appendix 4 and are treated as unsettled.
 * @param {Object|null|undefined} market
 * @returns {boolean}
 */
export function isCricketMarketUnsettledForDisplay(market) {
  if (!market) return false;
  const norm = cricketMarketAppendix4StatusForDisplay(market);
  if (!norm) return true;
  return norm === "UNSETTLED";
}

/**
 * True when `settled_status` is explicitly final (not UNSETTLED). Missing status → false (server may still block via cache).
 * @param {Object|null|undefined} market
 * @returns {boolean}
 */
export function isCricketMarketSettledForTrading(market) {
  if (!market) return false;
  const s = market.settled_status;
  if (s == null || s === "") return false;
  return !isCricketMarketUnsettledForDisplay(market);
}

/**
 * Decimal Appendix 4 on a single field (`settled_status`, `status`, etc.). Matches backend `cricket-settled-status`.
 * @param {unknown} settledStatus
 * @returns {boolean}
 */
export function isDecimalMarketSettledStatusFinal(settledStatus) {
  if (settledStatus == null || settledStatus === "") return false;
  /** Appendix 4: only 2–4 are final; numeric 0 is common on live objects and must not imply void/settled. */
  if (typeof settledStatus === "number") {
    return settledStatus === 2 || settledStatus === 3 || settledStatus === 4;
  }
  const s = String(settledStatus).trim();
  const upper = s.toUpperCase();
  if (upper === "UNSETTLED" || s === "1") return false;
  const n = parseInt(s, 10);
  if (!Number.isNaN(n) && n >= 2 && n <= 4) return true;
  return upper === "SETTLED" || upper === "VOID" || upper === "DEADHEAT";
}

/**
 * @param {Object|null|undefined} market
 * @returns {string|null} user-facing error or null
 */
export function getSettledMarketTradeBlockReason(market) {
  if (!isCricketMarketSettledForTrading(market)) return null;
  return "This market is settled. Trading is closed.";
}

/** Same rule as {@link hasValidOdds}: odds ≤ 1 are dead/SUS; values > 1 stay valid (e.g. 1.001). */
export const isCricketDecimalOddsEffectivelyOne = isOddsEffectivelyOne;

/**
 * @param {Object} [selection] - selection with recommended_price
 * @returns {boolean} true when price is usable for trading display (not effectively 1.00 / dead)
 */
export function hasValidCricketRecommendedOdds(selection) {
  if (!selection) return false;
  const p = parseFloat(String(selection.recommended_price));
  return Number.isFinite(p) && !isOddsEffectivelyOne(p);
}

/**
 * Decimal API: when probabilities are sent as 0.99/1.00 (uncertainty, injuries, modelling),
 * the market should be suspended. Client must also suspend in the UI.
 * Uses selection.probability only (0–1 scale). Not recommended_price (decimal odds like 1.10, 2.0).
 * We only suspend when some selection has p >= 0.99 (outcome nearly certain). We do NOT suspend
 * for low probability (e.g. p <= 0.01): long-odds selections like 101.00 (prob ~0.01) should
 * remain offerable for trading.
 * @param {Object} marketPrice - market_price_update market object with selections[].probability
 * @returns {boolean} true if market should be treated as suspended due to extreme probabilities
 */
export function hasExtremeProbabilities(marketPrice) {
  const selections = marketPrice?.selections ?? marketPrice?.selectionslist;
  if (!Array.isArray(selections)) return false;
  return selections.some((s) => {
    const p = parseFloat(s?.probability); // 0–1 only; odds (1.10, 2.0) are in recommended_price
    if (!Number.isFinite(p) || p < 0 || p > 1) return false; // ignore invalid or wrong-scale values
    if (p >= 0.99) return true; // near-certain outcome: suspend
    return false;
  });
}

/**
 * Over / wicket / boundary-in-over lines (not runs-off-delivery).
 */
const RUNS_IN_OVER_FAMILY_TYPE_IDS = new Set(["410", "415", "455", "460"]);

export function isRunsInOverFamilyMarket(market) {
  if (!market || typeof market !== "object") return false;
  const id = market.market_type_id != null ? String(market.market_type_id) : "";
  if (RUNS_IN_OVER_FAMILY_TYPE_IDS.has(id)) return true;
  const t = `${market.market_type || ""} ${market.market_name || ""} ${market.name || ""}`.toLowerCase();
  return t.includes("runs in over") || t.includes("wicket in over") || t.includes("boundary in over");
}

/**
 * True when this "runs in over N" line is for an over the scoreboard has already left.
 * Always false: hide/trade from Decimal display/active/status, not our over clock.
 */
export function isRunsInOverMarketPast(_market, _pos = {}) {
  return false;
}

function isDecimalTradingPausedStatus(status) {
  if (status == null || status === "") return false;
  const s = String(status).trim().toUpperCase();
  return s === "PAUSED" || s === "SUSPENDED";
}

/**
 * Whole-market suspend (show SUS on all runners). Per Decimal Integration Guide:
 * market `display=1` + `active=0` suspends the market; selection `active=0` hides that runner only.
 * Event status BALL_IN_PROGRESS is a cue to honour `active` / `bet_delay`, not to blanket-SUS.
 * Appendix 4 `UNSETTLED` is settlement state, not a trading pause.
 * Match Winner keeps last odds: ignore market active/status/extreme probs and match-level
 * `global_betting_status` / `betting_status` (Decimal turns that off for ball-in-play; MW often stays priced).
 *
 * @param {Object} marketOrPrice - Market or price-update (market-level active, status, selections/selectionslist)
 * @param {*} [_eventStatus]
 * @param {Object} [options] - { marketUpdate, selectionCount, curBall, curPos, innsNow, prematch }
 * @returns {{ suspended: boolean, showSUS: boolean }} showSUS = true when UI should display "SUS"
 */
export function getCricketMarketSuspendedState(marketOrPrice, _eventStatus = null, options = {}) {
  const { marketUpdate } = options;
  const getSel = (m) => m?.selectionslist ?? m?.selections ?? [];
  const selArray = getSel(marketOrPrice);
  const selectionCount = options.selectionCount !== undefined ? options.selectionCount : selArray.length;
  const isMatchWinner = isCricketMatchWinnerForTrade(marketOrPrice);

  // Match Winner: keep last odds. Do not treat Decimal active/status/extreme-prob as a board suspend.
  const activeSuspendedStrict =
    !isMatchWinner && isDecimalFlagZeroOrMissing(marketOrPrice?.active);
  const statusSuspended = !isMatchWinner && isDecimalTradingPausedStatus(marketOrPrice?.status);
  const matchTradingSuspended =
    !isMatchWinner &&
    marketUpdate != null &&
    (marketUpdate.betting_status === 0 ||
      marketUpdate.betting_status === "0" ||
      (typeof marketUpdate.betting_status === "string" &&
        marketUpdate.betting_status.toUpperCase() === "SUSPENDED"));
  const extremeProbs =
    !isMatchWinner && hasExtremeProbabilities(marketOrPrice) && selectionCount <= 2;

  const suspended = activeSuspendedStrict || statusSuspended || matchTradingSuspended || extremeProbs;
  return { suspended, showSUS: suspended };
}

/**
 * Returns true if the cricket match is finished (no trading allowed).
 * Uses same logic as match-play.jsx: event_status string (EVENT_CLOSED, MATCH_COMPLETE, FINISHED) or numeric (10, 11, 13).
 * @param {string|number} eventStatus - from market_update or match_update
 * @returns {boolean}
 */
export function isCricketMatchFinished(eventStatus) {
  return isMatchFinishedStatus(eventStatus);
}

/**
 * Format handicap/line for display: whole numbers without .0, keep .5 (e.g. 28 not 28.0, 28.5 stays 28.5).
 * @param {string|number} val - line or handicap value
 * @returns {string}
 */
export function formatHandicap(val) {
  const n = parseFloat(val);
  if (!Number.isFinite(n)) return String(val ?? "");
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Align with backend `RUNS_OFF_DELIVERY_MARKET_TYPE_IDS` + name match. */
const RUNS_OFF_DELIVERY_TYPE_IDS = new Set(["340", "342", "344"]);

function parsePositiveIntLocal(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseInt(String(v).trim(), 10);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

/** Same idea as backend `isRunsOffDeliveryMarket`. */
export function isRunsOffDeliveryMarket(market) {
  if (!market || typeof market !== "object") return false;
  const id = market.market_type_id != null ? String(market.market_type_id) : "";
  if (RUNS_OFF_DELIVERY_TYPE_IDS.has(id)) return true;
  return (market.market_type || "").toLowerCase().includes("runs off delivery");
}

/** Ball index from innings start: completed overs * 6 + ball (matches backend `getRunsOffDeliveryMarketBallIndex`). */
export function getRunsOffDeliveryMarketBallIndex(market) {
  if (!market || typeof market !== "object") return null;
  const over = parsePositiveIntLocal(market.over);
  const delivery = parsePositiveIntLocal(market.delivery);
  if (over != null && delivery != null) {
    return (over - 1) * 6 + delivery;
  }
  const name = String(market.market_name || market.name || "").trim();
  let mm = name.match(/Delivery\s+(\d+)\.(\d+)\s*$/i);
  if (!mm) mm = name.match(/(\d+)\.(\d+)\s*$/);
  if (!mm) return null;
  const fullOvers = parseInt(mm[1], 10);
  const ball = parseInt(mm[2], 10);
  if (Number.isNaN(fullOvers) || Number.isNaN(ball)) return null;
  return fullOvers * 6 + ball;
}

/** Parse scoreboard-style `curBall` e.g. "12.4" → ball index in innings. */
export function parseCurBallToBallIndex(curBall) {
  if (curBall == null || curBall === "") return null;
  const m = String(curBall).trim().match(/^(\d+)\.(\d+)/);
  if (!m) return null;
  const full = parseInt(m[1], 10);
  const ball = parseInt(m[2], 10);
  if (Number.isNaN(full) || Number.isNaN(ball)) return null;
  return full * 6 + ball;
}

/**
 * CurPos from DecimalSocketContext: [innings, Current Innings Overs, Current Innings Part Overs].
 * Same ball index as backend `getCurrentInningsPositionFromScoreboard` — aligns ROD checks with the events server.
 *
 * @param {unknown} curPos
 * @returns {number|null}
 */
export function parseCurPosToBallIndex(curPos) {
  if (!curPos || !Array.isArray(curPos) || curPos.length < 3) return null;
  const full = parsePositiveIntLocal(curPos[1]);
  const part = parsePositiveIntLocal(curPos[2]);
  if (full == null || part == null) return null;
  return full * 6 + part;
}

/**
 * Current innings ball index for ROD stale checks. Prefers agreement between sources:
 * `curBall` is built from Current Innings Overs + current.delv; `CurPos` uses Part Overs instead of delv —
 * those fields can lag each other. Using the maximum when both parse avoids allowing trades when either
 * stream shows we've moved past the delivery (matches server-side merged scoreboard).
 *
 * @param {{ curBall?: string|null; curPos?: unknown }} opts
 * @returns {number|null}
 */
export function getRunsOffDeliveryCurrentBallIndex({ curBall, curPos }) {
  const fromPos = parseCurPosToBallIndex(curPos);
  const fromBall = parseCurBallToBallIndex(curBall);
  const vals = [fromPos, fromBall].filter((v) => v != null && Number.isFinite(v));
  if (vals.length === 0) return null;
  return Math.max(...vals);
}

/**
 * Block trader submit when Runs off Delivery refers to a delivery the match has already passed (live only).
 * Pass `curPos` from socket context so checks match the events server (see `getRunsOffDeliveryCurrentBallIndex`).
 * Future-innings ROD is allowed when Decimal still offers it; only past innings / past balls are blocked.
 * @returns {string|null} user-facing error, or null if OK / not applicable / cannot determine
 */
export function getRunsOffDeliveryTradeBlockReason(market, { curBall, curPos, innsNow, prematch }) {
  if (prematch) return null;
  if (!isRunsOffDeliveryMarket(market)) return null;
  const mi = parsePositiveIntLocal(market.inns);
  const currentInns = parsePositiveIntLocal(innsNow);
  if (mi != null && currentInns != null && mi < currentInns) {
    return "That delivery has already passed. Refresh and place your trade on the current ball.";
  }
  // Future innings: do not apply current-innings ball index.
  if (mi != null && currentInns != null && mi > currentInns) {
    return null;
  }
  const marketBall = getRunsOffDeliveryMarketBallIndex(market);
  if (marketBall == null) return null;
  const posBall = getRunsOffDeliveryCurrentBallIndex({ curBall, curPos });
  if (posBall == null) return null;
  if (posBall > marketBall) {
    return "That delivery has already passed. Refresh and place your trade on the current ball.";
  }
  return null;
}

/** 1-based over currently being bowled (`22.3` → 23), matching Trader `floor(curBall)+1`. */
export function getCurrentBowlingOverNumber({ curBall, curPos }) {
  const fromBall = (() => {
    const n = Number.parseFloat(curBall);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.floor(n) + 1;
  })();
  const fromPos = (() => {
    if (!curPos || !Array.isArray(curPos) || curPos.length < 2) return null;
    const full = parsePositiveIntLocal(curPos[1]);
    if (full == null || full < 0) return null;
    return full + 1;
  })();
  const vals = [fromBall, fromPos].filter((v) => v != null && Number.isFinite(v));
  if (vals.length === 0) return null;
  return Math.max(...vals);
}

/**
 * No longer blocks trades for finished overs — follow Decimal display/active.
 * @returns {null}
 */
export function getCompletedOverTradeBlockReason(_market, _pos = {}) {
  return null;
}
